import {Fragment, useState, useEffect, useCallback} from 'react';
import {useStore} from '../../hooks/store';
import cx from 'classnames';
import {TransitionGroup, CSSTransition} from "react-transition-group";
import useHttp from '../../hooks/http';
import {scrollToTop, scrollToElement} from '../../utils';
import moment from 'moment-timezone';
import LoadingAnim from '../../UIComponents/LoadingAnim';
import CrossSell from './CrossSell';
import StatusMessage from '../../UIComponents/StatusMessage';
import {selectedDateFormat, selectedY, selectedM, selectedD} from '../../utils/dateFormat';

import {
    DayName,
    DateField,
    BlankDay,
    Day,
    CalendarWrapper,
    MonthWrapper,
    Header,
    TimeFilter,
    TimeFilterLabel,
    TimeFilterSide,
    MonthArrow,
    MonthName,
    WeekDays,
    Week,
    WeekTimes,
    Button,
    Legend,
    Color,
    EventDot,
    EventColor,
    CustomSelect,
    SelectPlaceholder,
    SelectOptions,
    SelectOption,
    AvailNumber,
    BlankSpot
} from '../../styles/calendar.js';
import {Space} from '../../styles/styles.js';

import {ToolTipCSS, CollapsibleAnimCSS} from '../../styles/css.js';

const MonthView = (props) => {
    const [state, dispatch] = useStore();
    const {isLoading, data, error, sendRequest, clear} = useHttp();
    const {
        dateContext,
        selectedDate,
        selectedDay,
        selectedTime,
        eventDates,
        weekdaysShort,
        months,
        clientState,
        totalPeople,
        totalTickets,
        showMonthAvailabilityMessage,
        section,
        calendarType,
        showCalendarModal
    } = state;
    const [eventTitles, setEventTitles] = useState([]);
    const [events, setEvents] = useState({});
    const [monthFieldActive, setMonthFieldActive] = useState(false);
    const [yearFieldActive, setYearFieldActive] = useState(false);
    const [showTimes, setShowTimes] = useState(false);
    const [today] = useState(moment().tz("Europe/London"));

    useEffect(() => {
        const eventsEndpoint = '/wp-json/tribe/events/v1/events?per_page=100';
        sendRequest(eventsEndpoint, 'GET');
    }, []);

    useEffect(() => {
        if (!isLoading && !error && data && data.events) {
            let dataTitles = [];
            let dataEvents = {};
            data.events.filter(item => {
                return moment(item.start_date).isSameOrAfter(moment());
            }).forEach(item => {
                let date = moment(item.start_date).format('Y-MM-DD');
                if (!eventTitles.includes(item.title)) {
                    if (item.title === 'Closed') eventTitles.unshift(item.title);
                    else eventTitles.push(item.title);
                }
                events[date] = item.title;
            });
            setEventTitles([...eventTitles, ...dataTitles]);
            setEvents({...events, ...dataEvents});
            if (data.next_rest_url) {
                sendRequest(data.next_rest_url, 'GET');
            }
        }
    }, [data]);

    useEffect(() => {
        if (!isLoading && error) {
            if (error.status === 400 && error.data.data.params.hasOwnProperty('_t')) {
                dispatch('SET', {
                    showSessionMessage: true,
                    timerActive: false
                });
            }
            console.log(error);
        }
    }, [error]);

    const firstDayOfMonth = () => {
        let firstDay = moment(dateContext).startOf('month').format('d'); // Day of week 0...1..5...6
        return firstDay;
    }
    const setMonth = (month) => {
        // console.log('setMonth');
        let monthNo = months.indexOf(month);
        const newDateContext = moment(dateContext).set("month", monthNo);

        // Compute the year from the new date context
        let y = Number(newDateContext.format('Y'));
        // console.log('Attempting to dispatch UPDATE_RID with year:', y);

        // Dispatch the UPDATE_RID action to update the RID based on the year
        dispatch('UPDATE_RID', {year: y});

        // dispatch('CLEAR_SELECTION');
        dispatch('SET', {
            dateContext: newDateContext,
            thisMonth: newDateContext.format('M'),
            fetchMonth: true,
            eventDates: null,
            monthChange: true
        });
    }
    const nextMonth = () => {
        // console.log('nextMonth');
        const d = dateContext.add(1, "month");
        let y = Number(d.format('Y'));
        // console.log(y);

        // Dispatch the action
        // console.log('Attempting to dispatch UPDATE_RID with year:', y);
        dispatch('UPDATE_RID', {year: y});

        // dispatch('CLEAR_SELECTION');
        dispatch('SET', {
            dateContext: d,
            thisMonth: d.format('M'),
            fetchMonth: true,
            eventDates: null
        });
    }
    const prevMonth = () => {
        // console.log('prevMonth');
        if (dateContext.isAfter(today, 'month')) {
            const d = dateContext.subtract(1, "month");
            let y = Number(d.format('Y'));
            // dispatch('CLEAR_SELECTION');
            // Dispatch the action
            // console.log('Attempting to dispatch UPDATE_RID with year:', y);
            dispatch('UPDATE_RID', {year: y});
            dispatch('SET', {
                dateContext: d,
                thisMonth: d.format('M'),
                fetchMonth: true,
                eventDates: null
            });
        }
    }
    const onMonthChange = (e) => {
        // console.log("onMonthChange");
        setMonth(e.target.value);
        setMonthFieldActive(!monthFieldActive);
    }
    const onSelectMonth = (m) => {
        // console.log("onSelectMonth");
        setMonth(m);
        setMonthFieldActive(false);
        setYearFieldActive(false);
    }
    const setYear = (year) => {
        // console.log('setYear with year:', year);
        let newDateContext = moment(dateContext).set("year", year);  // Adjust the current dateContext to the new year
        let newYear = Number(newDateContext.format("YYYY"));
        // console.log("Setting new year in date context to:", newYear);
        dispatch('SET', {
            dateContext: newDateContext,
            fetchMonth: true,  // Possibly trigger a month fetch conditionally if required
        });
        // console.log('newDateContext:', newDateContext); // Debugging the result

        dispatch('CLEAR_SELECTION');
        dispatch('UPDATE_RID', {year: year}); // Ensure RID is updated
        dispatch('SET', {
            dateContext: newDateContext,
            thisMonth: newDateContext.format('M'),
            fetchMonth: true,
            eventDates: null,
            monthChange: true
        });
    }
    const onYearChange = (e) => {
        // console.log("onYearChange");
        setYear(e.target.value);
        setYearFieldActive(!yearFieldActive);
    }
    const onSelectYear = (y) => {
        // console.log("onSelectYear with year:", y);
        setYear(y);
        setMonthFieldActive(false);
        setYearFieldActive(false);
    }
    const onDayClick = (e, day) => {
        // console.log('onDayClick', selectedDate);
        // console.log('selectedTime', selectedTime);
        let padDay = day < 10 ? '0' + day : day;
        const date = dateContext.format("Y") + '-' + dateContext.format("MM") + '-' + padDay;
        // console.log('date', date);
        // console.log('eventDates', eventDates);
        if (!showCalendarModal) {
            setTimeout(() => {
                scrollToElement('day' + day);
            }, 250);
        }
        // var hasAvailability = eventDates[date].find(d => {
        //     return d.numAvail > 0;
        // });
        // if (!hasAvailability) hasAvailability = false;
        setShowTimes(true);
        dispatch('SET', {
            selectedDay: parseInt(day),
            selectedDate: date,
            showAvailabilityMessage: false
        });
    }
    const onSelectTime = (item, go) => {
        // console.log('selected time', item);
        if (!go) return;
        if (!showCalendarModal) {
            setTimeout(() => {
                scrollToElement('ticketSummary');
            }, 500);
        }
        const dateY = selectedY(item.date);
        const date = selectedDateFormat(item.date);
        // console.log('DATE', date);
        const newState = {
            evdate: item.date,
            evid: item.eventID,
            evtime: item.date
        }
        const newCS = Object.assign(clientState, newState);
        // console.log('MONTH newCS', newCS);
        // console.log('item.time', item.time);
        setShowTimes(false);
        dispatch('SET', {
            dateContext: moment.utc(item.date),
            selectedDate: date,
            selectedTime: item.time,
            selectedEvent: item.eventID,
            selectedDateTime: item.date,
            selectedMonth: selectedM(item.date),
            selectedDay: parseInt(selectedD(item.date)),
            selectedYear: dateY,
            showAvailabilityMessage: false,
            availMaxed: item.numAvail === totalTickets,
            dayLimit: item.numAvail,
            dateTimeConfirmed: true,
            buttonDisabled: false,
            postUpdate: true,
            timerActive: true,
            showCalendarModal: false,
            showCalendar: false,
            checkParking: true,
            clientState: newCS
        });
    }
    const toggleMonthField = () => {
        setMonthFieldActive(!monthFieldActive);
    }
    const toggleYearField = () => {
        setYearFieldActive(!yearFieldActive);
    }

    let weekdays = weekdaysShort.map((day) => {
        return (
            <DayName key={day} className="week-day">{day}</DayName>
        )
    });

    let blanks = [];
    for (let i = 0; i < firstDayOfMonth(); i++) {
        blanks.push(<BlankDay key={i * 80}>
                {""}
            </BlankDay>
        );
    }
    // console.log(eventDates);
    let daysInMonth = [];
    if (eventDates) {
        const currentDate = today.format("Y") + '-' + today.format("MM") + '-' + today.format("DD");

        for (let d = 1; d <= dateContext.daysInMonth(); d++) {
            const padD = d < 10 ? '0' + d : d;
            const thisFullDate = dateContext.format("Y") + '-' + dateContext.format("MM") + '-' + padD;

            let eventDotClass = '', eventTitle = '';
            if (events[thisFullDate]) {
                let eventIndex = eventTitles.indexOf(events[thisFullDate]);
                eventDotClass = 'event' + eventIndex;
                eventTitle = events[thisFullDate];
            }
            daysInMonth.push(
                <Day key={d} id={'day' + d} className={cx({
                    'current-day': thisFullDate == currentDate,
                    'avail-day': Object.keys(eventDates).includes(thisFullDate),
                    'selected-day': selectedDate && selectedDate == thisFullDate,
                    'large': calendarType === 'redemption'
                })}>
                    {events[thisFullDate] && <div className="tooltip dot"><EventDot className={eventDotClass}/>
                        <div className={cx('top', eventDotClass)}>
                            <div className={cx('text', {'medium': eventTitle.length > 10})}
                                 dangerouslySetInnerHTML={{__html: eventTitle}}></div>
                            <i></i>
                        </div>
                    </div>}
                    <span onClick={(e) => {
                        onDayClick(e, d)
                    }}>{d}</span>
                </Day>
            );
        }
    }
    let monthList = months.map(data => {
        let selectStatus = (data === dateContext.format("MMMM") ? ' selected' : '');
        return (
            <option key={data} value={data} selected={selectStatus}>
                {data}
            </option>
        );
    });
    let monthList2 = months.map(data => {
        let formattedMonth = data.slice(0, 3);
        let selectStatus = (data === dateContext.format("MMMM") ? ' selected' : '');
        return (
            <SelectOption key={data} selected={selectStatus} onClick={() => onSelectMonth(data)}>
                {formattedMonth}
            </SelectOption>
        );
    });

    var totalSlots = [...blanks, ...daysInMonth];
    let rows = [];
    let cells = [];
    // console.log(totalSlots);
    totalSlots.forEach((row, i) => {
        if ((i % 7) !== 0) {
            cells.push(row);
        } else {
            let insertRow = cells.slice();
            rows.push(insertRow);
            cells = [];
            cells.push(row);
        }
        if (i === totalSlots.length - 1) {
            let insertRow = cells.slice();
            rows.push(insertRow);
        }
    });
    // if (selectedDate) console.log(selectedDate);
    let amButtons = null, pmButtons = null, hasTickets;
    let dayAvail = false;
    // console.log('eventDates', eventDates);
    if (eventDates && selectedDate && eventDates.hasOwnProperty(selectedDate)) {
        let amTimes = [], pmTimes = [];
        eventDates[selectedDate].forEach(t => {
            var am_pm = moment.utc(t.date).format('a');
            if (am_pm == 'am') amTimes.push(t);
            else if (am_pm == 'pm') pmTimes.push(t);
        });
        // console.log('selectedDate', selectedDate);
        // console.log('selectedTime', selectedTime);
        amButtons = amTimes.map(t => {
            hasTickets = totalPeople <= t.numAvail;
            const thisDate = selectedDateFormat(t.date);
            return (
                <Button
                    className={cx({
                        'selected': selectedDate == thisDate && selectedTime == t.time,
                        'disabled': !hasTickets
                    })}
                    disabled={!hasTickets}
                    onClick={() => onSelectTime(t, hasTickets)}>
                    {t.time} {hasTickets && <AvailNumber>{t.numAvail < 100 ? t.numAvail : '99+'}</AvailNumber>}
                </Button>
            );
        });
        pmButtons = pmTimes.map(t => {
            hasTickets = totalPeople <= t.numAvail;
            const thisDate = selectedDateFormat(t.date);
            return (
                <Button
                    className={cx({
                        'selected': selectedDate == thisDate && selectedTime == t.time,
                        'disabled': !hasTickets
                    })}
                    onClick={() => onSelectTime(t, hasTickets)}>
                    {t.time} {hasTickets && <AvailNumber>{t.numAvail < 100 ? t.numAvail : '99+'}</AvailNumber>}
                </Button>
            );
        });
        dayAvail = amButtons.length > 0 || pmButtons.length > 0 ? true : false;
    }
    let loadingText = 'Loading ' + dateContext.format('MMMM') + ' dates...';
    let dayCells = eventDates ?
        rows.map((d, i) => {
            let days = d.map(x => {
                return parseInt(x.key);
            });
            return (
                <Fragment>
                    <CollapsibleAnimCSS/>
                    <Week key={i * 100}>{d}</Week>
                    {showTimes && dayAvail && days.includes(selectedDay) && <CSSTransition
                        timeout={500}
                        classNames="collapsible"
                    >
                        <WeekTimes key={1000} className={cx({'active': days.includes(selectedDay)})}>
                            <TimeFilter>
                                <TimeFilterSide className="am">
                                    <TimeFilterLabel>AM</TimeFilterLabel>
                                    {amButtons}
                                </TimeFilterSide>
                                <TimeFilterSide className="pm">
                                    <TimeFilterLabel>PM</TimeFilterLabel>
                                    {pmButtons}
                                </TimeFilterSide>
                            </TimeFilter>
                            {!dayAvail && days.includes(selectedDay) && calendarType === 'booking' && <CrossSell/>}
                        </WeekTimes>
                    </CSSTransition>}
                </Fragment>
            );
        })
        : showMonthAvailabilityMessage ?
            <Space minHeight={250}><StatusMessage
                text="There are no available times. Please try another month."/></Space>
            : <MonthWrapper><LoadingAnim inline={false} text={loadingText}/></MonthWrapper>;

    let yearOptions = [2024, 2025].map(y => {
        let selectStatus = dateContext.format("Y") == y ? 'selected' : '';
        return (
            <option key={y} value={y} selected={selectStatus}>{y}</option>
        )
    });
    let yearOptions2 = [2024, 2025].map(y => {
        let selectStatus = dateContext.format("Y") == y ? 'selected' : '';
        return (
            <SelectOption key={y} selected={selectStatus} onClick={() => onSelectYear(y)}>{y}</SelectOption>
        )
    });

    const EventLegend = () => {
        var thisMonthEvents = Object.keys(events).filter(date => {
            return moment(date).isSame(dateContext, 'month');
        });
        var titles = [];
        thisMonthEvents.forEach(d => {
            titles.push(events[d]);
        });
        var unique = new Set(titles);
        var uniqueArr = [...unique];
        return uniqueArr.map(d => {
            let eventIndex = eventTitles.indexOf(d);
            let eventDotClass = 'event' + eventIndex;
            return (
                <EventColor className={cx('large', eventDotClass)}>
                    {eventTitles[eventIndex]}
                </EventColor>
            );
        });
    }

    return (
        <Fragment>
            <ToolTipCSS/>
            <CalendarWrapper>
                <Header>
                    <MonthArrow className={cx({'disabled': !moment(dateContext).isAfter(today, 'month')})}>
                        <i className="prev fa fa-fw fa-chevron-left" onClick={() => prevMonth()}></i>
                    </MonthArrow>
                    <MonthName>
                        <form>
                            <CustomSelect className={cx({'active': monthFieldActive})}>
                                <SelectPlaceholder className="select_placeholder"
                                                   onClick={toggleMonthField}>{moment(dateContext).format('MMM')}</SelectPlaceholder>
                                <SelectOptions className="select_options">
                                    {monthList2}
                                </SelectOptions>
                                <DateField numItems={monthList.length} onChange={(e) => onMonthChange(e)}>
                                    {monthList}
                                </DateField>
                            </CustomSelect>
                            <CustomSelect className={cx({'active': yearFieldActive})}>
                                <SelectPlaceholder className="select_placeholder"
                                                   onClick={toggleYearField}>{moment(dateContext).format('Y')}</SelectPlaceholder>
                                <SelectOptions className="select_options">
                                    {yearOptions2}
                                </SelectOptions>
                                <DateField numItems={yearOptions.length} onChange={(e) => onYearChange(e)}>
                                    {yearOptions}
                                </DateField>
                            </CustomSelect>
                        </form>
                    </MonthName>
                    <MonthArrow className="next">
                        <i className="prev fa fa-fw fa-chevron-right" onClick={() => nextMonth()}></i>
                    </MonthArrow>
                </Header>
                <WeekDays>{weekdays}</WeekDays>
                {dayCells}
            </CalendarWrapper>
            <Legend>
                <Color className="available">Available</Color>
                <Color className="soldOut">Sold Out</Color>
                <Color className="selected">Selected</Color>
            </Legend>
            <Legend>
                <AvailNumber className="icon"/> Number of tickets remaining
            </Legend>
            <Legend>
                <EventLegend/>
            </Legend>
        </Fragment>
    );
}

export default MonthView;
